import { CheckedAddress } from '@/domain/types/CheckedAddress'

export enum InterestRate {
  Stable = 1,
  Variable = 2,
}

export const NATIVE_ASSET_MOCK_ADDRESS = CheckedAddress('0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE')

export const MAX_INT = BigInt(2 ** 256 - 1)
export const MAX_UINT160 = 2n ** 160n - 1n

// Reserve some native HYPE for gas when depositing the full balance
export const NATIVE_HYPE_GAS_RESERVE = 0.001

export const QUERY_REFETCH_INTERVAL = 2000
export const QUERY_STALE_TIME = 2000

// Listing/overview pages (markets hub, earn, borrow hero). Much cheaper to
// refresh infrequently since these aggregate many reserves/vaults/pairs and
// don't drive the live UX of a user editing a position.
export const LISTING_REFETCH_INTERVAL = 30_000
export const LISTING_STALE_TIME = 30_000

// Onchain data polling intervals (for RPC-heavy batch reads)
export const ONCHAIN_STALE_TIME = 30_000 // 30 seconds
export const ONCHAIN_GC_TIME = 60_000 // 60 seconds
export const ONCHAIN_REFETCH_INTERVAL = 30_000 // 30 seconds

// Indexer data polling intervals (API responses, less expensive)
export const INDEXER_STALE_TIME = 30_000 // 30 seconds
export const INDEXER_GC_TIME = 60_000 // 60 seconds
export const INDEXER_REFETCH_INTERVAL = 30_000 // 30 seconds

// Structural/semi-static data (LTV configs, token metadata)
export const STRUCTURAL_STALE_TIME = 60_000 // 1 minute
export const STRUCTURAL_GC_TIME = 120_000 // 2 minutes
export const STRUCTURAL_REFETCH_INTERVAL = 60_000 // 1 minute

export const ZUSTAND_APP_STORE_LOCAL_STORAGE_KEY = 'zustand-app-store'
export const ZUSTAND_APP_STORE_LOCAL_STORAGE_VERSION = 2

export const apiUrl = process.env.NEXT_PUBLIC_API_URL ?? '/api'
export const blockAnaliticaApiUrl = process.env.NEXT_PUBLIC_BLOCK_ANALITICA_API_URL ?? '/ba-api'
export const twitterFollowUrl = process.env.NEXT_PUBLIC_TWITTER_FOLLOW_URL ?? '/verify-follow'

export const HYPURR_UI_REFERRAL_CODE = 0 // 0x0
export const HYPURR_UI_REFERRAL_CODE_BIGINT = BigInt(HYPURR_UI_REFERRAL_CODE)

export const USDXL_ADDRESS = CheckedAddress('0xca79db4B49f608eF54a5CB813FbEd3a6387bC645')
export const A_USDXL_ADDRESS = CheckedAddress('0x7911c2c9c5f5a6f4Bef58d7dF35903abd3EE9DD6')

// Hide reserves on the UI
export const reserveBlacklist: string[] = []

// Deprecated isolated pairs - hidden from UI
export const isolatedPairBlacklist: string[] = [
  '0xBC3FD25d4f88D8fa4C6Cc54b2eb5f623dDF5Ec22', // HLP0
  '0xa89bFE5222841fcE6D2602A74535Fc1C3C9Cc07E', // UPUMP_USDXL
].map((a) => a.toLowerCase())

export const VIEM_TIMEOUT_ON_FORKS = 60_000 // forks tend to be slow. This improves reliability/performance. Default is 10_000

export const MERKL_BASE = 'https://api-merkl.angle.money/v4'
export const FUUL_BASE = 'https://api.fuul.xyz/api/v1'
export const POINTS_ENABLED = false

export const PERMIT2_ADDRESS = CheckedAddress('0x000000000022d473030f116ddee9f6b43ac78ba3')

// Isolated pairs
export const USDXL_LHYPE_PAIR_ADDRESS = CheckedAddress('0xAeedD5B6d42e0F077ccF3E7A78ff70b8cB217329')
export const PURR_USDXL_PAIR_ADDRESS = CheckedAddress('0x8001e1e7b05990d22DD8Cdb9737f9fE6589827cE')
export const MHYPE_WHYPE_PAIR_ADDRESS = CheckedAddress('0xE4847Cb23dAd9311b9907497EF8B39d00AC1DE14')
export const HFUN_USDXL_PAIR_ADDRESS = CheckedAddress('0xDC87120DdFC236db5E48e8E0Fee0C4b8c323B64b')
export const CMETH_UETH_PAIR_ADDRESS = CheckedAddress('0x64dF7B4DA81a26A59417857f508B39370138D95e')
export const SUSDE_USDXL_PAIR_ADDRESS = CheckedAddress('0x9136199C9002af633e4d24e4D1c8cCa5192d3dAf')
export const BUDDY_USDXL_PAIR_ADDRESS = CheckedAddress('0x26672D3ef84b53Ddbcd3732e9bc96c3712Ad758d')
export const USOL_USDT0_PAIR_ADDRESS = CheckedAddress('0x34E3d3834B79D77d8558307535Cdf4871B64Bc65')
export const LHYPE_USDT0_PAIR_ADDRESS = CheckedAddress('0x1C5164A764844356d57654ea83f9f1B72Cd10db5')
export const XAUT0_USDT0_PAIR_ADDRESS = CheckedAddress('0x6ecbc0FBE25664C9a1f55aA773f91B77b60E860c')
export const HWHLP_USDT0_PAIR_ADDRESS = CheckedAddress('0x2c910F67DbF81099e6f8e126E7265d7595DC20aD')
export const HWHLP_USDHL_PAIR_ADDRESS = CheckedAddress('0xe8648B00570B5562488d8324c98242EE8FB1A35F')
export const WHLP_USDT0_PAIR_ADDRESS = CheckedAddress('0x6f344dB6b76BE7E110D8AD37d14D5A9F03e2db03')
export const UPUMP_USDXL_PAIR_ADDRESS = CheckedAddress('0xa89bFE5222841fcE6D2602A74535Fc1C3C9Cc07E')
export const PTKHYPE_HYPE_PAIR_ADDRESS = CheckedAddress('0x1aA7e637f62642d85f65d2a0795ec4B8C85a9095')
export const PTHWHLP_USDT0_PAIR_ADDRESS = CheckedAddress('0x543DBF5C74C6fb7C14f62b1Ae010a3696e22E3A0')
export const HAHYPE_HYPE_PAIR_ADDRESS = CheckedAddress('0x14148d1876bD40a39DB1ED09675A81e14199C394')
export const HBUSDT_USDT0_PAIR_ADDRESS = CheckedAddress('0xcDA6D421d5edB4267D99B4b66Dd423Ca1B8d4410')
export const PTVKHYPE_HYPE_PAIR_ADDRESS = CheckedAddress('0x32BE2B5989e9452b6e1B993b49eA0181eB0a5A83')

/**
 * Indexer URL for client-side requests (goes through proxy)
 * @see /app/api/indexer/[...path]/route.ts
 */
export const API_INDEXER_URL = '/api/indexer'

/**
 * Indexer origin URL for server-side direct access (internal use only)
 * This should only be used by the proxy route itself
 */
export const API_INDEXER_ORIGIN = process.env.INDEXER_ORIGIN_URL ?? ''

// Euler config - re-exported from centralized euler-contracts.ts
export {
  EULER_ORACLE_CHECKS_URL,
  EULER_SWAP_API_URL,
  EULER_USD_UNIT_OF_ACCOUNT,
  EVC_ADDRESS,
  E_EARN_VAULT_CHAIN_ID,
  E_LEND_CHAIN_ID,
} from './euler-contracts'

export const LIFI_API_URL = 'https://li.quest/v1'

export const DEFAULT_SLIPPAGE = 2e-3
const configuredAppDomain = process.env.NEXT_PUBLIC_APP_DOMAIN?.trim()

export const APP_DOMAIN =
  configuredAppDomain && configuredAppDomain.length > 0
    ? configuredAppDomain
    : process.env.NODE_ENV === 'development'
      ? 'localhost:4000'
      : 'hypurrfi.com'

export const APP_ORIGIN = APP_DOMAIN.includes('localhost') ? `http://${APP_DOMAIN}` : `https://${APP_DOMAIN}`

export const APP_HOSTNAME = new URL(APP_ORIGIN).hostname

export const HOST = APP_ORIGIN

export const SWAP_VAULT_CONTRACT = CheckedAddress('0xc4c4D41ba446fbFE44c9b450E7A1aD82312488e0')

export const REFERRAL_REGISTRY_ADDRESS = CheckedAddress('0xd696e6b94ce79fe9aac288c0fc761643219510b6')
export const HYPURR_REFERRAL_KEY = 'hypurrfi-0'

export const BUILDER_CODE_ADDRESS = CheckedAddress(
  process.env.NEXT_PUBLIC_BUILDER_CODE_ADDRESS ?? '0x67A6D918C3aDFc36B047A8684491481114f6b3A6',
)
export const BUILDER_CODE_FEE = (process.env.NEXT_PUBLIC_BUILDER_CODE_FEE ?? '0.01%') as `${string}%`

export const ENSO_API_KEY = process.env.NEXT_PUBLIC_ENSO_API_KEY ?? 'f2c90621-251f-45cf-9362-c477d6fe9748'

// Brahma Console Cards API Configuration
export const CARD_API_ENDPOINT = process.env.NEXT_PUBLIC_CARD_API_ENDPOINT ?? 'https://cards-api.hypurrfi.com'
export const CARD_PARTNER_ID = process.env.NEXT_PUBLIC_CARD_PARTNER_ID ?? '0a27ace0-7272-4a65-9122-93ee530806b6'
export const CARD_AUTH_VERSION = '1'
export const CARD_TELEGRAM_BOT_USERNAME = process.env.NEXT_PUBLIC_CARD_TELEGRAM_BOT_USERNAME ?? 'hypurrfi_card_test_bot'

export const HYPERCORE_SPOT_ID = 4294967295
export const HYPERCORE_PERPS_ID = 0

export const EVAULT_SIMULATOR_ADDRESS = CheckedAddress('0xDe81b7138a84fc5ff97a2ae076D4E8c85ab36ca7')

export const KNOWN_PROTOCOLS = [
  '0x5748ae796AE46A4F1348a1693de4b50560485562',
  '0x300b7bA0Cbf10661f886a4d3696Ee3E942852B4E',
  '0x197F1a807C3188dD6ce38759ACc796721FC35292',
  '0x20c56e5B70a44FbdCBacA6265F9c848e59bf7C39',
  '0xC8256411E5Addb9064920F04C45C566111b380e4',
  '0xa98eC637538eE3e7bbC4928a29727aFb05831f3e',
  '0xA66B5862FD471A1b1EeC8710071f3d085F79d5e4',
  '0xFbCE30C40D7AC30484c719FE3857C8eFb6D08659',
  '0x5064D3E2906317905f1d59663D5Fa257c15a704a',
  '0x1C020F03305acD09994C1910D440646E4a5F91B0',
  '0xf545003323DA8419Ce95dD4137eC90577D420eA1',
  '0x5996E9FAE2885df0640B9C2e50676E47a48d78C0',
].map((address) => address.toLowerCase())
